// Converts a snake_case contract key to a human-readable Title Case label.
// Special-cases healthcare acronyms so they render in their canonical form,
// and strips trailing numeric tokens to keep them as bare digits.
//
// Examples:
//   claim_filing_indicator  → "Claim Filing Indicator"
//   billing_provider_npi    → "Billing Provider NPI"
//   date_of_birth           → "Date of Birth"
//   payer_id_qualifier      → "Payer ID Qualifier"
//   address_line_1          → "Address Line 1"

/** Acronyms that must preserve their canonical ALL-CAPS (or mixed) form. */
const ACRONYMS = new Map([
  ['npi', 'NPI'],
  ['tin', 'TIN'],
  ['dob', 'DOB'],
  ['id', 'ID'],
  ['icd', 'ICD'],
  ['cpt', 'CPT'],
  ['hcpcs', 'HCPCS'],
  ['pos', 'POS'],
  ['eob', 'EOB'],
  ['ssn', 'SSN'],
  ['ein', 'EIN'],
  ['mrn', 'MRN'],
  ['zip', 'ZIP'],
  ['dx', 'Dx'],
  ['cms', 'CMS'],
  ['url', 'URL'],
]);

/**
 * Convert a snake_case field key to a display label.
 *
 * @param {string} key - e.g. "billing_provider_npi" or "address_line_1"
 * @returns {string} - e.g. "Billing Provider NPI" or "Address Line 1"
 */
export default function humanizeFieldLabel(key) {
  if (!key || typeof key !== 'string') return String(key ?? '');

  return key
    .split('_')
    .map((token) => {
      const lower = token.toLowerCase();

      // Pure trailing number — keep as-is (e.g. "1", "2").
      if (/^\d+$/.test(token)) return token;

      // Known acronym — return canonical form.
      if (ACRONYMS.has(lower)) return ACRONYMS.get(lower);

      // Default: Title-case the token.
      return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
    })
    .join(' ');
}
